import { updateAuthUserProfile } from "@/api/resources/auth/api";
import { getBrowserTimezone } from "@/providers/dates";
import { getTimezoneOffset } from "@/utils/dates";
import { setPosthogUser } from "@/utils/posthog";
import { setSentryUser } from "@/utils/sentry";

import type { JwtPayload } from "@supabase/supabase-js";

import type { UserProfile } from "@/supabase.types";

export const setUserAnalytics = (profile: UserProfile) => {
  setSentryUser(profile);
  setPosthogUser(profile);
};

export const setInitialUserTimezone = async (profile: UserProfile) => {
  try {
    const currentTimezone = getBrowserTimezone();
    const supportedTimezones = Intl.supportedValuesOf("timeZone");

    // abort if we can't determine the current timezone, or the timezone determined isn't valid
    if (!currentTimezone || !supportedTimezones.includes(currentTimezone)) return;

    // abort if the user's timezone is already set (we assume it was set deliberately to anything other than UTC)
    if (profile.timezone && profile.timezone !== "UTC") return;

    // abort if the user's timezone and UTC have the same offset === 0
    if (getTimezoneOffset(profile.timezone) === getTimezoneOffset("UTC")) return;

    // user timezone is not set (initial DB value 'UTC'), set it to current timezone
    const updatedProfile = await updateAuthUserProfile({
      id: profile.id,
      timezone: currentTimezone,
    });
    return updatedProfile;
  } catch (e) {
    console.error(e);
  }
};

export const syncSSOUserAttributes = async (user?: JwtPayload | null) => {
  // exit, if no user provided, user not logged in via SSO, or doesn't have custom claims (SSO attribute mappings)
  if (!user) return;
  if (!user.app_metadata?.provider?.includes("sso")) return;
  if (!user.user_metadata?.custom_claims) return;
  const customClaims = user.user_metadata.custom_claims as Record<string, string | undefined>;
  if (Object.keys(customClaims).length <= 0) return;

  try {
    await updateAuthUserProfile({
      id: user.sub, // user UUID
      first_name: customClaims.first_name ?? undefined,
      last_name: customClaims.last_name ?? undefined,
      phone: customClaims.phone ?? undefined, // eg. '+4912345678'
      locale: customClaims.locale ?? undefined, // eg. 'de
      timezone: customClaims.timezone ?? undefined, // eg. 'Europe/Berlin'
    });
  } catch (error) {
    console.error(`Failed to sync SSO user attributes: `, error);
  }
};
